import * as DialogPrimitive from "@radix-ui/react-dialog"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import { Suspense, lazy, useEffect, useMemo } from "react"
import { cn } from "~/common/cn"

const JourneyMap = lazy(() => import("~/maps/journey-map").then((m) => ({ default: m.JourneyMap })))

export type LightboxLog = {
  id: string
  authorDisplayName: string
  completedOn: string
  city?: string | null
  state?: string | null
  comment?: string | null
  photoUrl?: string | null
  photoThumbUrl?: string | null
  latitude?: number | null
  longitude?: number | null
  displayOnMap: boolean
}

type Props = {
  logs: LightboxLog[]
  openLogId: string | null
  onOpenChange: (open: boolean) => void
  onNavigate: (logId: string) => void
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })

export const JourneyPhotoLightbox = ({ logs, openLogId, onOpenChange, onNavigate }: Props) => {
  const photoLogs = useMemo(() => logs.filter((l) => Boolean(l.photoUrl)), [logs])
  const currentIndex = openLogId ? photoLogs.findIndex((l) => l.id === openLogId) : -1
  const current = currentIndex >= 0 ? photoLogs[currentIndex] : null
  const prev = currentIndex > 0 ? photoLogs[currentIndex - 1] : null
  const next =
    currentIndex >= 0 && currentIndex < photoLogs.length - 1 ? photoLogs[currentIndex + 1] : null

  useEffect(() => {
    if (!current) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && prev) {
        e.preventDefault()
        onNavigate(prev.id)
      } else if (e.key === "ArrowRight" && next) {
        e.preventDefault()
        onNavigate(next.id)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [current, prev, next, onNavigate])

  if (!current) return null

  const showMap =
    current.displayOnMap &&
    typeof current.latitude === "number" &&
    typeof current.longitude === "number"

  return (
    <DialogPrimitive.Root open onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[1100] bg-black/90 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[1101] flex h-[90vh] w-[90vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg bg-white shadow-2xl focus:outline-none lg:flex-row"
        >
          <div className="relative flex flex-1 items-center justify-center bg-black">
            <img
              src={current.photoUrl ?? undefined}
              alt={`Journey log photo by ${current.authorDisplayName}`}
              className="max-h-full max-w-full object-contain"
            />

            {prev ? (
              <button
                type="button"
                onClick={() => onNavigate(prev.id)}
                aria-label="Previous photo"
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white transition hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
              >
                <ChevronLeft className="h-6 w-6" aria-hidden />
              </button>
            ) : null}

            {next ? (
              <button
                type="button"
                onClick={() => onNavigate(next.id)}
                aria-label="Next photo"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white transition hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
              >
                <ChevronRight className="h-6 w-6" aria-hidden />
              </button>
            ) : null}

            {photoLogs.length > 1 ? (
              <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
                {currentIndex + 1} / {photoLogs.length}
              </div>
            ) : null}
          </div>

          <aside
            className={cn(
              "w-full shrink-0 border-t bg-white p-5 lg:w-80 lg:overflow-y-auto lg:border-l lg:border-t-0"
            )}
          >
            <DialogPrimitive.Title className="text-base font-semibold leading-snug">
              {current.authorDisplayName}
            </DialogPrimitive.Title>

            {current.city || current.state ? (
              <p className="mt-1 text-sm text-stone-600">
                {[current.city, current.state].filter(Boolean).join(", ")}
              </p>
            ) : null}

            <p className="mt-1 text-sm text-stone-500">
              <time dateTime={current.completedOn}>{formatDate(current.completedOn)}</time>
            </p>

            {showMap ? (
              <Suspense
                fallback={<div className="mt-3 h-32 animate-pulse rounded-lg bg-stone-100" />}
              >
                <JourneyMap
                  stops={[
                    {
                      id: current.id,
                      latitude: current.latitude as number,
                      longitude: current.longitude as number,
                    },
                  ]}
                  interactive={false}
                  initialZoom={9}
                  className="mt-3 h-32 w-full"
                />
              </Suspense>
            ) : null}

            {current.comment ? (
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-stone-800">
                {current.comment}
              </p>
            ) : null}
          </aside>

          <DialogPrimitive.Close
            aria-label="Close"
            className="absolute right-3 top-3 rounded-full bg-black/60 p-2 text-white transition hover:bg-black/80 focus:outline-none focus:ring-2 focus:ring-white"
          >
            <X className="h-5 w-5" aria-hidden />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
